import { useState } from "react";

import {
  ACTIVITY_PAGE_LIMIT_DEFAULT,
  ACTIVITY_PAGE_LIMIT_MAX,
} from "@thalamos/haven-common";

/** How a list's page grows: by `step` at a time, up to what one request may carry. */
export type Growth = { step: number; max: number };

export const ACTIVITY_LOG_GROWTH: Growth = {
  step: ACTIVITY_PAGE_LIMIT_DEFAULT,
  max: ACTIVITY_PAGE_LIMIT_MAX,
};

/**
 * The page size to ask for next, or nothing once the server will serve no more
 * in one request.
 *
 * Stated rather than inferred from what came back, so the control goes at the
 * moment it stops being able to fetch anything rather than one press later.
 */
export const nextLimit = (current: number, growth: Growth): number | null =>
  current >= growth.max ? null : Math.min(current + growth.step, growth.max);

/**
 * The page size a list is showing, and how to ask for more of it.
 *
 * A larger page rather than a further one: the whole list is re-read, so the
 * rows stay in one order and one added while somebody reads cannot fall into
 * the seam between two pages. `onShowMore` is absent once no further page can
 * be asked for.
 */
export const useGrowingLimit = (
  growth: Growth,
  /** Whose list this is; another's starts again at one step. */
  of?: string,
): {
  limit: number;
  onShowMore: (() => void) | undefined;
} => {
  const [grown, setGrown] = useState({ of, limit: growth.step });

  // Started again while rendering rather than in an effect, so the first
  // request for another list already asks for one step.
  if (grown.of !== of) {
    setGrown({ of, limit: growth.step });
  }

  const limit = grown.of === of ? grown.limit : growth.step;
  const further = nextLimit(limit, growth);

  return {
    limit,
    onShowMore:
      further === null
        ? undefined
        : () => {
            setGrown({ of, limit: further });
          },
  };
};
